import { useState as useGlobalState } from "../hooks/useReducer";
import { useCallback, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { rem } from "polished";
import OverviewHeader from "../components/OverviewHeader";
import OverviewList from "../components/OverviewList";
import OverviewListShipItem from "../components/OverviewListShipItem";
import ShipDetailHeader from "../components/ShipDetailHeader";
import ShipDetailBody from "../components/ShipDetailBody";
import RoundButton from "../components/RoundButton";
import Dropdown from "../components/Dropdown";
import { StyledDropDownItem } from "../components/Dropdown";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  getVoyagesByShip,
  getVoyageTypesByIDs,
  shipByID,
  getArchiveItemsById,
  getBlogItemsById,
} from "../api";
import { useTranslation } from "react-i18next";
import { useDispatch } from "../hooks/useReducer";
import { getTranslatedShipType } from "../utils/helperFunctions";
import { useMatomo } from "@datapunt/matomo-tracker-react";

const StyledPanel = styled.section`
  ${({ theme, $detail }) => `
    position: fixed;
    top: 0;
    bottom: ${rem(56)};
    left: 0;
    width: 100%;
    @media only screen and (min-width: ${theme.breakPoint}px) {
        bottom: 0;
        width: calc(100% - ${rem(167)});
        left: ${$detail ? rem(-358) : rem(167)}};
        transition: all ${theme.transition.timing} ${theme.transition.function};
    }
    `}
`;

const StyledOverviewContainer = styled.div`
  ${({ theme, $detail }) => `
        position: absolute;
        bottom: 0;
        top: 0; 
        z-index: 0;
        overflow-y: ${$detail ? "hidden" : "scroll"};
        overflow-x: ${$detail ? "unset" : "hidden"};

        @media only screen and (min-width: ${theme.breakPoint}px) {
          transition: all ${theme.transition.timing} ${
    theme.transition.function
  };
            width: 100%;
        }
    `}
`;

const StyledDetailPanel = styled.div`
  ${({ theme, $detail }) => `
        background-color: ${theme.color.key.light};
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        left: ${$detail ? "0" : "100%"};
        transition: left ${theme.transition.timing} ${
    theme.transition.function
  };
        z-index: 10;
        overflow-y: auto;
        display: flex;
        flex-direction: column;

        @media only screen and (min-width: ${theme.breakPoint}px) {
            transition: none;
            left: ${$detail ? "100%" : "0"};
            width: ${$detail ? `${rem(525)}` : "0"};
        }
    `}
`;

const StyledRoundButton = styled(RoundButton)`
  ${({ theme, $hasLinks }) => `
      position: absolute;
      bottom: ${rem(-12)};
      right: ${rem(16)};
      z-index: 1;
    `}
`;

const Ships = () => {
  const { ships, schip_types } = useGlobalState();
  const { shipId } = useParams();
  const [shipData, setShipData] = useState(null);
  const [voyageData, setVoyageData] = useState([]);
  const [voyageTypeIDs, setVoyageTypeIDs] = useState([]);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [archiveItems, setArchiveItems] = useState(null);
  const [blogItems, setBlogItems] = useState(null);
  const [filteredShips, setFilteredShips] = useState(ships);
  const navigate = useNavigate();
  const ref = useRef();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { trackPageView, trackEvent } = useMatomo();

  const { data, error } = useQuery(shipByID(shipId), {
    skip: shipId === undefined,
  });

  // Track page view
  useEffect(() => {
    trackPageView({
      documentTitle: "Ships", // optional
    });
  }, []);

  const handleCloseDetail = useCallback(() => {
    setShipData(null);
    navigate("/ships");
  }, [navigate]);

  const handleDocumentClick = useCallback(
    (event) => {
      if (
        ref &&
        !ref.current.contains(event.target) &&
        event.target.tagName !== "A"
      ) {
        handleCloseDetail();
      }
    },
    [handleCloseDetail]
  );

  useEffect(() => {
    if (data !== undefined || error !== undefined) {
      if (error) {
        dispatch({ type: "SET_ERROR" });
      } else {
        if (data.schepen_by_id !== null) {
          setShipData(data.schepen_by_id);
          document.addEventListener("mousedown", handleDocumentClick);
        } else {
          document.removeEventListener("mousedown", handleDocumentClick);
        }
      }

      return () => {
        document.removeEventListener("mousedown", handleDocumentClick);
      };
    }
  }, [data, error, dispatch, handleDocumentClick]);

  useEffect(() => {
    if (shipId !== undefined) {
      getVoyagesByShip(shipId).then((data) => {
        setVoyageData(data.data);
        setVoyageTypeIDs(
          data.data.map(({ type }) => {
            return type;
          })
        );
      });

      getArchiveItemsById(shipId).then((data) => {
        if (data.data !== undefined || data.error !== undefined) {
          if (data.error) {
            dispatch({ type: "SET_ERROR" });
          } else {
            setArchiveItems(data.data);
          }
        }
      });

      if (i18n.language !== null) {
        getBlogItemsById(shipId, i18n.language).then((data) => {
          if (data.data !== undefined || data.error !== undefined) {
            if (data.error) {
              dispatch({ type: "SET_ERROR" });
            } else {
              setBlogItems(data.data);
            }
          }
        });
      }
    }
  }, [shipId, i18n.language, dispatch]);

  useEffect(() => {
    if (voyageTypeIDs.length > 0) {
      getVoyageTypesByIDs(voyageTypeIDs).then((data) => {
        setVoyageData((voyages) => {
          return voyages.map((voyage) => {
            voyage.voyage = data.data.find((e) => e.id === voyage.type);
            return voyage;
          });
        });
      });
    }
  }, [voyageTypeIDs]);

  useEffect(() => {
    if (
      selectedOption === null ||
      selectedOption === "Alle schepen" ||
      selectedOption === "All ships"
    ) {
      setFilteredShips(ships);
    } else {
      setFilteredShips(
        ships.filter(
          ({ scheepstype }) =>
            scheepstype.naam.toLowerCase() === selectedOption.toLowerCase()
        )
      );
    }
  }, [ships, selectedOption]);

  const handlePageFilter = (id, value) => {
    setSelectedOption(value);
    setIsOpenDropdown(false);
  };

  const handleOpenFilter = () => {
    setIsOpenDropdown(!isOpenDropdown);
  };

  return (
    <StyledPanel $detail={shipData}>
      <StyledOverviewContainer $detail={shipData}>
        <OverviewHeader isDetailOpen={shipId !== undefined}>
          <Dropdown
            isOpen={isOpenDropdown}
            selectedOption={
              getTranslatedShipType(selectedOption, i18n.language) ||
              t(`ships.headerDefault`)
            }
            items={ships}
            onDataClick={handlePageFilter}
            onDropDownClick={handleOpenFilter}
          >
            <StyledDropDownItem
              index={0}
              onClick={() => handlePageFilter(null, t(`ships.headerDefault`))}
            >
              {t(`ships.headerDefault`)}
            </StyledDropDownItem>
            {schip_types.map((ship, index) => {
              return (
                <StyledDropDownItem
                  key={index + 1}
                  onClick={() => handlePageFilter(ship.id, ship.naam)}
                >
                  {getTranslatedShipType(ship.naam, i18n.language)}
                </StyledDropDownItem>
              );
            })}
          </Dropdown>
        </OverviewHeader>
        <OverviewList>
          {filteredShips.map((item, index) => {
            return (
              <OverviewListShipItem
                item={item}
                key={index}
                isDetailOpen={shipId !== undefined}
                isSelected={shipId === item.id}
              />
            );
          })}
        </OverviewList>
      </StyledOverviewContainer>
      {shipData && (
        <StyledDetailPanel $detail={shipData} ref={ref}>
          <ShipDetailHeader
            data={shipData}
            links={
              archiveItems && archiveItems.filter((item) => item.is_overview)
            }
          >
            <StyledRoundButton
              id="close"
              onClick={handleCloseDetail}
              $hasLinks={
                archiveItems && archiveItems.filter((item) => item.is_overview)
              }
            />
          </ShipDetailHeader>
          <ShipDetailBody
            data={shipData}
            voyageData={voyageData}
            archiveItems={archiveItems}
            blogItems={blogItems}
          />
        </StyledDetailPanel>
      )}
    </StyledPanel>
  );
};

export default Ships;
